import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';
import { localStorageSetItem, localStorageGetItem } from '../../utils/storage-available';
import { getListData, getReferralData, getSourceTypeData, getReminderMethodData, getReasonsListData, getContactMethodListData, getSalutationOptions, getFinanceTermOptions } from '../../utils/getReferralData';

// ----------------------------------------------------------------

const safeParse = (key, transform) => {
    try {
        const raw = localStorageGetItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return transform ? transform(parsed) : parsed;
    } catch { return []; }
};

const initialState = {
    preferredContactMethodData: safeParse('PreferredContactMethodData', getListData),
    referral: safeParse('ReferralData', getReferralData),
    sourceType: safeParse('SourceTypeData', getSourceTypeData),
    reminderMethod: safeParse('ReminderMethodData', getReminderMethodData),
    reminderPriority: safeParse('ReminderPriorityData', getListData),
    reasons: safeParse('ReasonsData', getReasonsListData),
    contactMethod: safeParse('ContactMethodData', getContactMethodListData),
    appointmentType: safeParse('AppointmentTypeData', getListData),
    salutationOptions: safeParse('SalutationOptions', getSalutationOptions),
    appointmentMethod: safeParse('AppointmentMethodData', getListData),
    archiveReasons: (localStorageGetItem('ArchiveReasonsData') || []),
    financeTermOptions: safeParse('FinanceTermOptions', getFinanceTermOptions),
    reportType: safeParse('ReportTypeData', getListData),
    refundPaymentReason: safeParse('RefundPaymentReasonData', getListData),
    practiceChoiceCategory: safeParse('PracticeChoiceCategoryEnum'),
    usageType: safeParse('UsageTypeEnum'),
};

export const slice = createSlice({
    name: 'schema',
    initialState,
    reducers: {
        setPreferredContactMethod(state, action) {
            state.preferredContactMethodData = action.payload;
        },
        setReferral(state, action) {
            state.referral = action.payload;
        },
        setSourceType(state, action) {
            state.sourceType = action.payload;
        },
        setReminderMethod(state, action) {
            state.reminderMethod = action.payload;
        },
        setReminderPriority(state, action) {
            state.reminderPriority = action.payload;
        },
        setReasons(state, action) {
            state.reasons = action.payload;
        },
        setArchiveReasons(state, action) {
            state.archiveReasons = action.payload;
        },
        setContactMethod(state, action) {
            state.contactMethod = action.payload;
        },
        setAppointmentType(state, action) {
            state.appointmentType = action.payload;
        },
        setSalutationOptions(state, action) {
            state.salutationOptions = action.payload;
        },
        setAppointmentMethod(state, action) {
            state.appointmentMethod = action.payload;
        },
        setFinanceTermOptions(state, action) {
            state.financeTermOptions = action.payload;
        },
        setReportType(state, action) {
            state.reportType = action.payload;
        },
        setRefundPaymentReason(state, action) {
            state.refundPaymentReason = action.payload;
        },
        setPracticeChoiceCategory(state, action) {
            state.practiceChoiceCategory = action.payload || [];
        },
        setUsageType(state, action) {
            state.usageType = action.payload || [];
        },
    }
});

export default slice.reducer;

// drf-spectacular appends auto-generated hash suffixes to enum names
// (e.g. ContactedVia812Enum). These change whenever the backend models change.
// findEnum() searches by prefix + a known value so the lookup never breaks.
function findEnum(schemas, prefix, knownValue, excludeValue) {
    // Try exact match first (stable names like PriorityEnum, NoteEnum)
    if (schemas?.[prefix]?.enum) return schemas[prefix].enum;
    // Fuzzy: find any key starting with prefix whose enum contains knownValue
    const key = Object.keys(schemas || {}).find(
        (k) => k.startsWith(prefix) && schemas[k]?.enum?.includes(knownValue)
            && (!excludeValue || !schemas[k]?.enum?.includes(excludeValue))
    );
    return key ? schemas[key].enum : undefined;
}

export function getSchemaList() {
    return async () => {
        try {
            const response = await callApi('/api/v1/schema/?format=json&fields={components{schemas}}');
            if (response.status === 200) {
                const schemas = response?.data?.components?.schemas;

                const preferredContact = findEnum(schemas, 'PreferredContactMethod', 'EMAIL');
                dispatch(slice.actions.setPreferredContactMethod(getListData(preferredContact)));
                localStorageSetItem('PreferredContactMethodData', JSON.stringify(preferredContact));

                const referral = findEnum(schemas, 'Referral', 'FACEBOOK');
                dispatch(slice.actions.setReferral(getReferralData(referral)));
                localStorageSetItem('ReferralData', JSON.stringify(referral));

                const sourceType = findEnum(schemas, 'Type', 'TELEPHONE');
                dispatch(slice.actions.setSourceType(getSourceTypeData(sourceType)));
                localStorageSetItem('SourceTypeData', JSON.stringify(sourceType));

                const reminderMethod = findEnum(schemas, 'Method', 'CALL');
                dispatch(slice.actions.setReminderMethod(getReminderMethodData(reminderMethod)));
                localStorageSetItem('ReminderMethodData', JSON.stringify(reminderMethod));

                const reminderPriority = findEnum(schemas, 'Priority', 'HIGH');
                dispatch(slice.actions.setReminderPriority(getListData(reminderPriority)));
                localStorageSetItem('ReminderPriorityData', JSON.stringify(reminderPriority));

                const notes = findEnum(schemas, 'Note', 'FOLLOW_UP');
                dispatch(slice.actions.setReasons(getReasonsListData(notes?.filter((ele) => (ele !== 'AWAITING_RESPONSE' && ele !== 'FOLLOW_UP')))));
                localStorageSetItem('ReasonsData', JSON.stringify(notes?.filter((ele) => (ele !== 'AWAITING_RESPONSE' && ele !== 'FOLLOW_UP'))));
                // Only pass archive reasons that the BE actually accepts.
                // TODO: extend ARCHIVE_VALID_REASONS when BE supports additional values
                // (e.g. CHANGED_MIND, TOO_EXPENSIVE, NO_RESPONSE).
                const ARCHIVE_VALID_REASONS = new Set(['NOT_INTERESTED', 'DUPLICATE_ENTRY']);
                const archiveValidNotes = (notes || []).filter((n) => ARCHIVE_VALID_REASONS.has(n));
                dispatch(slice.actions.setArchiveReasons(archiveValidNotes));
                localStorageSetItem('ArchiveReasonsData', JSON.stringify(archiveValidNotes));

                // ContactedVia — two enums exist: one with ONLINE_BOOKING (lead source), one without
                // (how the lead was contacted). We want the one WITHOUT ONLINE_BOOKING.
                const contactedVia = findEnum(schemas, 'ContactedVia', 'WHATSAPP', 'ONLINE_BOOKING');
                dispatch(slice.actions.setContactMethod(getContactMethodListData(contactedVia)));
                localStorageSetItem('ContactMethodData', JSON.stringify(contactedVia));

                const appointmentType = findEnum(schemas, 'Type', 'CONSULTATION');
                dispatch(slice.actions.setAppointmentType(getListData(appointmentType)));
                localStorageSetItem('AppointmentTypeData', JSON.stringify(appointmentType));

                const salutation = findEnum(schemas, 'Salutation', 'MR');
                dispatch(slice.actions.setSalutationOptions(getSalutationOptions(salutation)));
                localStorageSetItem('SalutationOptions', JSON.stringify(salutation));

                const appointmentMethod = findEnum(schemas, 'Method', 'IN_PRACTICE');
                dispatch(slice.actions.setAppointmentMethod(getListData(appointmentMethod)));
                localStorageSetItem('AppointmentMethodData', JSON.stringify(appointmentMethod));

                const financeTerm = findEnum(schemas, 'Term', null) || schemas?.TermEnum?.enum;
                dispatch(slice.actions.setFinanceTermOptions(getFinanceTermOptions(financeTerm)));
                localStorageSetItem('FinanceTermOptions', JSON.stringify(financeTerm));

                const reportType = findEnum(schemas, 'ReportType', 'PRACTICE_USER_LEAD_CONVERSIONS');
                dispatch(slice.actions.setReportType(getListData(reportType)));
                localStorageSetItem('ReportTypeData', JSON.stringify(reportType));

                const refundReason = findEnum(schemas, 'Reason', 'TOO_EXPENSIVE');
                dispatch(slice.actions.setRefundPaymentReason(getListData(refundReason)));
                localStorageSetItem('RefundPaymentReasonData', JSON.stringify(refundReason));

                // Practice Choices Category Enum
                const practiceCategory = findEnum(schemas, 'PracticeChoiceCategory', null) || schemas?.PracticeChoiceCategoryEnum?.enum || [];
                dispatch(slice.actions.setPracticeChoiceCategory(practiceCategory));
                localStorageSetItem('PracticeChoiceCategoryEnum', JSON.stringify(practiceCategory));

                // Usage Type Enum
                const usageType = findEnum(schemas, 'UsageType', 'MANUAL') || schemas?.UsageTypeEnum?.enum || [];
                dispatch(slice.actions.setUsageType(usageType));
                localStorageSetItem('UsageTypeEnum', JSON.stringify(usageType));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            console.error("Error Occured in Schema.js ::", error);
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}
